import {
  Autocomplete,
  Box,
  FormControl,
  FormLabel,
  TextField,
} from "@mui/material";
import { InventoryAxios } from "api/InventoryAxios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { MecordColors } from "Constants/Color";
import { useDispatch } from "react-redux";
import { addReorderLevel, updateReorderLevel } from "slices/Inventory/ReorderLevel/InStock/thunk";
import { fetchProductsByDropDown, fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { autocompleteDropdownStyles } from "Constants/Style";
import { clearAllTableStates } from "utils/clearTableStates";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";

interface ReorderLevelFormProps {
  initialData?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormValues {
  product: any;
  category: string;
  sub_category: string;
  type: string;
  make: string;
  model: string;
  minimum_quantity: string;
}

const ReorderLevelForm: React.FC<ReorderLevelFormProps> = ({
  initialData,
  onClose,
  onSuccess,
}) => {
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<string[]>([]);
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [makeOptions, setMakeOptions] = useState<string[]>([]);
  const dispatch = useDispatch<any>();
  const [products, setProducts] = useState<any[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [modelLoading, setModelLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const productOptionsForAutocomplete = products || [];

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      product: initialData?.product || null,
      category: initialData?.product?.category || "",
      sub_category: initialData?.product?.sub_category || "",
      type: initialData?.product?.type || "",
      make: initialData?.product?.make || "",
      model: initialData?.product?.model_id || initialData?.product?.model?.model_id || initialData?.product?.model || "",
      minimum_quantity: initialData?.reorder_level || "",
    },
    validationSchema: Yup.object({
      product: Yup.object().nullable().required("Product is required"),
      model: Yup.string().required("Model is required"),
      minimum_quantity: Yup.number()
        .required("Minimum quantity is required")
        .min(0, "Must be at least 0"),
    }),
    onSubmit: async (values) => {
      const payload = {
        product_id: values.product?.product_id || values.product?.id,
        reorder_level: values.minimum_quantity,
        remark: "", // Add remark if needed in UI
      };
      setIsSubmitting(true);

      try {
        if (initialData?.reorder_level_id) {
          await dispatch(
            updateReorderLevel({ id: initialData.reorder_level_id, values: payload })
          ).unwrap();
          toast.success("Reorder Level updated successfully");
        } else {
          await dispatch(addReorderLevel(payload)).unwrap();
          clearAllTableStates()
          toast.success("Reorder Level created successfully");
        }
        if (onSuccess) onSuccess();
        onClose();
      } catch (error: any) {
        console.error("ReorderLevelForm submit error:", error);
        const errorMessage = error?.message || error?.data?.message || error || "";
        if (
          errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("duplicate") ||
          error?.status === 409
        ) {
          toast.error("Item already in listing");
        } else {
          toast.error(errorMessage || "Something went wrong");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Fetch initial dropdown options
  // useEffect(() => {
  //   InventoryAxios.get("/api/reorder-level/dependent-dropdown/")
  //     .then((res) => {
  //       setItemNameOptions(res.data?.item_name || []);
  //       if (!initialData) {
  //         setCategoryOptions(res.data?.category_name || []);
  //       }
  //     })
  //     .catch(() => toast.error("Error fetching items"));
  // }, [initialData]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelLoading(true);
        const result = await dispatch(fetchModelByDropDown()).unwrap();
        setModels(result.data || []);
      } catch (error) {
        console.error("Failed to fetch models:", error);
        setModels([]);
      } finally {
        setModelLoading(false);
      }
    };
    fetchModels();
  }, [dispatch]);

  // Handle model selection and fetch products
  const handleModelChange = async (modelId: string, shouldClearProduct = true) => {
    if (shouldClearProduct) {
      formik.setFieldValue("product", null);
      formik.setFieldValue("category", "");
      formik.setFieldValue("sub_category", "");
      formik.setFieldValue("type", "");
      formik.setFieldValue("make", "");
    }

    if (modelId) {
      try {
        setProductLoading(true);
        const result = await dispatch(
          fetchProductsDropDownByModel(modelId)
        ).unwrap();
        setProducts(result.data || []);
      } catch (error) {
        console.error("Failed to fetch products for model:", modelId, error);
        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    } else {
      setProducts([]);
    }
  };

  // Fetch initial products if editing
  useEffect(() => {
    const productData = initialData?.product;
    const modelIdOrName = productData?.model_id || productData?.model?.model_id || productData?.model;

    if (modelIdOrName && typeof modelIdOrName === 'string') {
      if (models.length > 0) {
        const foundModel = models.find(m =>
          (m.model_id || m.id) === modelIdOrName ||
          m.model_name === modelIdOrName
        );
        if (foundModel) {
          const resolvedId = foundModel.model_id || foundModel.id;
          if (resolvedId) {
            formik.setFieldValue("model", resolvedId);
            handleModelChange(resolvedId, false);
          }
        } else {
          handleModelChange(modelIdOrName, false);
        }
      } else {
        handleModelChange(modelIdOrName, false);
      }
    }
  }, [initialData, models]);

  useEffect(() => {
    if (initialData?.product && products.length > 0) {
      const selectedId = initialData.product.product_id || initialData.product.id;
      const fullProduct = products.find(
        (p: any) => (p.product_id || p.id) === selectedId
      );
      if (fullProduct) {
        if (!formik.values.category)
          formik.setFieldValue(
            "category",
            fullProduct.category_name ||
            fullProduct.category?.category_name ||
            ""
          );
        if (!formik.values.sub_category)
          formik.setFieldValue(
            "sub_category",
            fullProduct.sub_category_name ||
            fullProduct.sub_category?.sub_category_name ||
            ""
          );
        if (!formik.values.type)
          formik.setFieldValue(
            "type",
            fullProduct.product_types?.length > 0
              ? fullProduct.product_types
                .map((t: any) => t.type_name)
                .join(", ")
              : ""
          );
        if (!formik.values.make)
          formik.setFieldValue(
            "make",
            fullProduct.make_name || fullProduct.make?.make_name || ""
          );
        if (!formik.values.model || formik.values.model === fullProduct.model_name)
          formik.setFieldValue(
            "model",
            fullProduct.model_id || fullProduct.model?.model_id || fullProduct.model_name || fullProduct.model?.model_name || ""
          );
      }
    }
  }, [products, initialData, formik.setFieldValue, formik.values.model]);

  const fetchDependentOptions = (
    params: any,
    targetSetter: (data: any) => void
  ) => {
    InventoryAxios.get("/api/reorder-level/dependent-dropdown/", {
      params,
    }).then((res) => {
      if (params.category_name)
        setSubCategoryOptions(res.data?.subcategory_name || []);
      if (params.subcategory_name) setTypeOptions(res.data?.type_name || []);
    });
  };

  const labelStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#000",
    marginBottom: "8px",
    display: "block",
  };

  return (
    <Form
      onSubmit={formik.handleSubmit}
      className="p-2 px-3 pb-2 border-bottom border-1 border-light"
    >
      <Row className="g-2">
        {/* Category */}
        {/* <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Category</Form.Label>
            <Autocomplete
              size="small"
              disabled={true}
              options={categoryOptions}
              value={formik.values.category}
              onChange={(_, value) => {
                formik.setFieldValue("category", value);
                formik.setFieldValue("sub_category", "");
                if (value)
                  fetchDependentOptions(
                    { category_name: value },
                    setSubCategoryOptions
                  );
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Category"
                  error={formik.touched.category && !!formik.errors.category}
                />
              )}
            />
          </Form.Group>
        </Col> */}

        {/* Sub-category */}
        {/* <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Sub-category</Form.Label>
            <Autocomplete
              size="small"
              disabled={true}
              options={subCategoryOptions}
              value={formik.values.sub_category}
              onChange={(_, value) => {
                formik.setFieldValue("sub_category", value);
                if (value)
                  fetchDependentOptions(
                    { subcategory_name: value },
                    setTypeOptions
                  );
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Sub-Category"
                  error={
                    formik.touched.sub_category && !!formik.errors.sub_category
                  }
                />
              )}
            />
          </Form.Group>
        </Col> */}

        {/* Type */}
        {/* <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Type</Form.Label>
            <Autocomplete
              size="small"
              disabled={true}
              options={[]} // No dynamic options needed, value is synced from product
              value={formik.values.type}
              renderInput={(params) => (
                <BootstrapTextField {...params} placeholder="Select Type" />
              )}
            />
          </Form.Group>
        </Col> */}

        {/* Make */}
        {/* <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Make</Form.Label>
            <Autocomplete
              size="small"
              disabled={true}
              options={makeOptions}
              value={formik.values.make}
              onChange={(_, value) => formik.setFieldValue("make", value)}
              renderInput={(params) => (
                <BootstrapTextField {...params} placeholder="Select Make" />
              )}
            />
          </Form.Group>
        </Col> */}

        {/* Model */}
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Model</Form.Label>
            <Autocomplete
              size="small"
              options={models}
              loading={modelLoading}
              value={
                models.find(
                  (m) => (m.model_id || m.id) === formik.values.model
                ) || null
              }
              getOptionLabel={(option: any) => option.model_name || ""}
              isOptionEqualToValue={(option: any, value: any) => {
                const optId = option.model_id || option.id;
                const valId = value.model_id || value.id || value;
                return optId === valId;
              }}
              noOptionsText={modelLoading ? "Loading..." : "No models available"}
              onChange={(_, value) => {
                const modelId = value?.model_id || value?.id || "";
                formik.setFieldValue("model", modelId);
                handleModelChange(modelId);
              }}
              disabled={!!initialData}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>{option.model_name}</span>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Model"
                  sx={autocompleteDropdownStyles.inputSx}
                  error={formik.touched.model && !!formik.errors.model}
                  helperText={(formik.touched.model && formik.errors.model) as string}
                />
              )}
            />
          </Form.Group>
        </Col>

        {/* Product */}
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Product</Form.Label>
            <Autocomplete
              size="small"
              options={productOptionsForAutocomplete}
              loading={productLoading}
              getOptionLabel={(option) => option.product_name || option.name || ""}
              value={formik.values.product}
              isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
              noOptionsText={productLoading ? "Loading..." : "No products available"}
              onChange={(_, value) => {
                formik.setFieldValue("product", value);
                formik.setFieldValue("category", value?.category_name || value?.category?.category_name || "");
                formik.setFieldValue("sub_category", value?.sub_category_name || value?.sub_category?.sub_category_name || "");
                formik.setFieldValue("type", value?.product_types?.length > 0 ? value.product_types.map((t: any) => t.type_name).join(", ") : "");
                formik.setFieldValue("make", value?.make_name || value?.make?.make_name || "");
                formik.setFieldValue("model", value?.model_id || value?.model?.model_id || value?.model_name || value?.model?.model_name || "");
              }}
              disabled={!!initialData}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{ fontWeight: "bold", marginBottom: "4px" }}
                      >
                        {option.product_name || option.name}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Make: {option.make?.make_name || "N/A"}</span>
                        <span>
                          Model: {option.model?.model_name || "N/A"}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "2px",
                        }}
                      >
                        <span>
                          SKU: {option.product_sku || option.sku || "N/A"}
                        </span>
                        <span>
                          HSN:{" "}
                          {option.hsn?.hsn_code || option.hsn_code || "N/A"}
                        </span>
                        <span
                          style={{ fontWeight: "bold", color: "#007bff" }}
                        >
                          ₹{option.unit_price || "N/A"}
                        </span>
                      </div>
                    </div>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Product"
                  size="small"
                  error={formik.touched.product && !!formik.errors.product}
                  helperText={(formik.touched.product && formik.errors.product) as string}
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
          </Form.Group>
        </Col>

        {/* Minimum Quantity */}
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label style={labelStyle}>Minimum Quantity</Form.Label>
            <Form.Control
              type="number"
              name="minimum_quantity"
              placeholder="Enter Quantity"
              style={{
                fontSize: "13px",
                height: "38px",
                borderColor: "#ced4da",
                borderRadius: ".25rem",
              }}
              value={formik.values.minimum_quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.minimum_quantity &&
                !!formik.errors.minimum_quantity
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.minimum_quantity}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <hr
        className="mt-3 mb-4"
        style={{ backgroundColor: "#e9ebec", opacity: 0.5 }}
      />

      <div className="d-flex justify-content-center gap-3 pb-3">
        {/* <Button
          onClick={onClose}
          style={{
            backgroundColor: MecordColors.NeutralButtonBGColor,
            color: MecordColors.NeutralButtonTextColor,
            border: "none",
            padding: "8px 30px",
            fontSize: "14px",
            fontWeight: "600",
            borderRadius: "6px",
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          style={{
            backgroundColor: MecordColors.PrimaryButtonBGColor,
            color: "#fff",
            border: "none",
            padding: "8px 30px",
            fontSize: "14px",
            fontWeight: "600",
            borderRadius: "6px",
          }}
        >
          Submit
        </Button> */}

         <CancelButton
              // redirectTo="/master/category"
              disabled={isSubmitting}
              onClick={onClose}
            />
            <PrimarySubmitButton
              label={initialData?.reorder_level_id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={initialData?.reorder_level_id  ? "Updating..." : "Submitting..."}
            />
      </div>
    </Form>
  );
};

export default ReorderLevelForm;
